import { useCallback, useState } from 'preact/hooks';

import { isLongform } from '../utils/assetHelpers';
import { getAssetAccess } from '../../../utils/player/asset';
import { PREVIEW_THRESHOLD_IN_SECONDS } from '../layouts/UpNextPreviewLayout/UpNextPreviewLayout';

import { usePlayerEvent } from './usePlayerEvent';
import { usePlayer } from './PlayerProvider';
import { useNextAsset } from './NextAssetProvider/NextAssetProvider';
import { usePaywallState } from './usePaywallState';
import { useConfig } from './ConfigProvider';
import { useJWPlayerEvent } from './useJWPlayerEvent';
import { usePlayNext } from './usePlayNext';
import { useCast } from './CastProvider';

export const useUpNext = () => {
  const player = usePlayer();
  const { playNext } = usePlayNext();
  const { recommended } = useConfig();
  const { nextAsset } = useNextAsset();
  const { hasAccess } = usePaywallState(
    nextAsset ? getAssetAccess(nextAsset) : undefined
  );
  const { isActive: isCastActive } = useCast();

  const [state, setState] = useState<
    'idle' | 'preview' | 'post-countdown' | 'canceled' | 'playNext'
  >('idle');

  const isUpNextSupported = recommended.autoplay && Boolean(nextAsset);

  const handleAssetReady = useCallback(() => {
    setState('idle');
  }, []);

  const handlePlayNext = useCallback(() => {
    setState('playNext');
  }, []);

  usePlayerEvent('playlistItem', handleAssetReady);
  usePlayerEvent('playNext', handlePlayNext);

  const handleCancel = useCallback(() => {
    setState((prevState) => {
      if (prevState === 'preview' || prevState === 'post-countdown') {
        return 'canceled';
      }

      return prevState;
    });
  }, []);

  const handleTime = useCallback(
    ({ position, duration }: jwplayer.TimeParam) => {
      if (
        !isLongform(duration) ||
        isCastActive ||
        !isUpNextSupported ||
        state !== 'idle'
      ) {
        return;
      }

      if (duration - position < PREVIEW_THRESHOLD_IN_SECONDS) {
        setState('preview');
      }
    },
    [isUpNextSupported, state, isCastActive]
  );

  useJWPlayerEvent('time', handleTime);

  const handleComplete = useCallback(
    (reason?: string) => {
      // if the reason is defined, it means that player has been stopped or playNext method
      // has been called, in both cases we shouldn't trigger autoplay next
      if (reason || !isUpNextSupported || state === 'canceled') {
        return;
      }

      if (isLongform(player.getDuration()) || isCastActive) {
        if (hasAccess) {
          playNext(nextAsset!, {
            pulse: { playbackSource: 'autoplayNext-related' },
          });
        }
      } else {
        setState('post-countdown');
      }
    },
    [isUpNextSupported, state, nextAsset, hasAccess, isCastActive]
  );

  usePlayerEvent('complete', handleComplete);

  return {
    upNextState: isUpNextSupported ? state : 'idle',
    handleUpNextCancel: handleCancel,
  };
};
